import { vaccineConfigSlugPattern } from "@/consts/vaccination-slugs"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { getStoryblokApi } from "@/lib/storyblok"
import { getCampaignsByVaccineSlug } from "@/storyblok/stories"
import { mergeVaccineCampaign } from "@/storyblok/vaccinationRelations"
import {
  ServiceTypeStoryblok,
  VaccinationClinicTypeStoryblok,
  VaccineTypeListStoryblok,
  VaccineTypeStoryblok
} from "@/types/component-types"
import { defaultSbParams } from "@lib/defaultSbParams"
import { ISbStories, ISbStoryData, SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import { ExpandableCardGrid } from "./ExpandableCardGrid"
import { ServicePriceCard } from "./ServicePriceCard"
import { VaccinationPriceCard } from "./VaccinationPriceCard"

// Renders a set of vaccines as price cards. The slug values come from the `vaccine-types`
// datasource; we fetch each vaccine's config story and render its card.
//
// A vaccine's config lives under `vaccination/vaccin/` or `vaccination/resevaccin/` depending on its
// type, and the datasource value doesn't say which. The wildcard matches either, so this blok never
// has to know about the split — and keeps working if a vaccine is later re-typed and moved.
export const VaccineTypeList = async ({
  blok,
  services,
  vaccination_clinic
}: BlokProps<VaccineTypeListStoryblok> & {
  services: ServiceTypeStoryblok[]
  vaccination_clinic?: VaccinationClinicTypeStoryblok
}) => {
  /**
   * The clinic's own `offered_vaccines` is the source of truth for WHICH vaccines it offers; this
   * blok's `vaccines` is an optional override for which of them to show, and in what order.
   *
   * Held in two places, both edited by hand, the two drift — and the failure is silent and
   * asymmetric: the clinics overview filters on `offered_vaccines`, so a clinic whose page sells a
   * vaccine its config omits simply vanishes when a patient filters for it. Falling back means the
   * default cannot drift, while an editor who wants a specific subset or order still gets one by
   * filling the field.
   *
   * Only clinic pages pass `vaccination_clinic`; anywhere else this blok keeps its own list, so an
   * empty `vaccines` there renders nothing exactly as before.
   */
  const slugs = blok.vaccines?.length ? blok.vaccines : (vaccination_clinic?.offered_vaccines ?? [])
  const title = blok?.title
  const initialCount = blok?.show_initial_count

  let stories: ISbStoryData<VaccineTypeStoryblok>[] = []
  if (slugs.length > 0) {
    const bySlugs = slugs.map(vaccineConfigSlugPattern).join(",")
    const { data } = (await getStoryblokApi().get("cdn/stories", {
      ...defaultSbParams,
      by_slugs: bySlugs
    })) as ISbStories

    // Preserve the editor's chosen order (by_slugs doesn't guarantee it). Keyed on the vaccine's
    // own slug rather than the full path, since the wildcard means the folder isn't known here.
    const bySlug = new Map(
      (data.stories as ISbStoryData<VaccineTypeStoryblok>[]).map(story => [story.full_slug.split("/").at(-2), story])
    )
    // Campaigns are their own stories; merged in here so the cards read them where they always did.
    const campaignsByVaccineSlug = await getCampaignsByVaccineSlug()

    stories = slugs
      .map(slug => bySlug.get(slug))
      .filter((story): story is ISbStoryData<VaccineTypeStoryblok> => Boolean(story))
      .map(story => ({
        ...story,
        content: mergeVaccineCampaign(story.content, campaignsByVaccineSlug[story.full_slug.split("/").at(-2) ?? ""])
      }))
  }

  if (stories.length === 0) return null

  const card = (story: ISbStoryData<VaccineTypeStoryblok>) => (
    <VaccinationPriceCard key={story.uuid} story={story} showBookAction={false} />
  )

  const serviceCard = (service: ServiceTypeStoryblok) => (
    <ServicePriceCard key={service._uid} blok={service} showBookAction={false} />
  )

  const visibleCount = initialCount || 6
  const serviceCards = (services ?? []).map(serviceCard)
  const vaccineSlots = Math.max(0, visibleCount - serviceCards.length)

  const initial = [...stories.slice(0, vaccineSlots).map(card), ...serviceCards]
  const rest = stories.slice(vaccineSlots).map(card)

  return (
    <section {...storyblokEditable(blok as SbBlokData)} className="bg-surface py-[18px] lg:py-10">
      <div className="mx-auto w-full max-w-[1155px] px-4 lg:px-8">
        {title && <h2 className="mb-6 text-2xl font-bold">{title}</h2>}
        {rest.length > 0 ? (
          <ExpandableCardGrid initial={initial} rest={rest} />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">{initial}</div>
        )}
      </div>
    </section>
  )
}

export default VaccineTypeList
